import { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress,
  Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, LinearProgress, Link, MenuItem, Select, Stack,
  TextField, Typography,
} from '@mui/material';
import { useProject } from '../../hooks/ProjectContext';
import Iconify from '../../components/iconify';
import MiniMapPreview from '../../components/map/MiniMapPreview';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ALL_CLAIMS = [
  { key: 'land_type', label: 'Land Use Type', desc: 'Cropland or grassland (VM0042 Sec 4)', section: 'boundary' },
  { key: 'ownership', label: 'Ownership & Carbon Rights', desc: 'Legal right to control project activities (VCS 3.7)', section: 'eligibility' },
  { key: 'no_clearing', label: '10-Year No-Clearing', desc: 'No clearing of natural vegetation in prior 10 years', section: 'boundary' },
  { key: 'water_bodies', label: 'Water Bodies Delineated', desc: 'Water bodies excluded from eligible area', section: 'boundary' },
  { key: 'infrastructure', label: 'Infrastructure Excluded', desc: 'Roads, buildings excluded from boundary', section: 'boundary' },
  { key: 'soil_type', label: 'Soil Classification', desc: 'Mineral soil; not drained peatland', section: 'boundary' },
  { key: 'use_history', label: 'Land Use History', desc: 'Baseline scenario, rotation, evidence of no land-use change', section: 'eligibility' },
  { key: 'productivity', label: '5% Productivity Rule', desc: 'Yield not decreasing >5%', section: 'eligibility' },
];
const BOUNDARY_CLAIMS = ALL_CLAIMS.filter(c => c.section === 'boundary');
const ELIGIBILITY_CLAIMS = ALL_CLAIMS.filter(c => c.section === 'eligibility');
const CLAIM_STATUSES = ['empty', 'in_progress', 'done', 'verified'];

const PHASES = [
  { key: 'boundary', label: 'Boundary & Area', icon: 'eva:map-outline', color: '#1976d2',
    desc: 'Define project boundary per site, delineate water bodies and infrastructure.',
    projectItems: ['Geographic reference: country, region, coordinates', 'Grouped project declaration', 'Project proponent information'] },
  { key: 'eligibility', label: 'Eligibility', icon: 'eva:checkmark-circle-2-outline', color: '#2e7d32',
    desc: 'Verify VM0042 applicability conditions per site with evidence.',
    projectItems: ['AFOLU category: ALM (Improved Agricultural Land Management)', 'Baseline scenario description (common across sites)'] },
  { key: 'carbon', label: 'Carbon Potential', icon: 'eva:trending-up-outline', color: '#ed6c02',
    desc: 'Estimate removal rate (tCO2e/ha/yr) using IPCC defaults or site-specific data.',
    projectItems: ['Methodology version: VM0042 v2.2', 'Quantification approach: QA1 / QA2 / QA3'] },
  { key: 'additionality', label: 'Additionality', icon: 'eva:shield-outline', color: '#9c27b0',
    desc: 'Barrier analysis, common practice assessment, regulatory surplus.',
    projectItems: ['Investment barrier analysis', 'Common practice for region', 'Regulatory surplus confirmation'] },
  { key: 'financial', label: 'Financial Model', icon: 'eva:bar-chart-outline', color: '#0288d1',
    desc: 'Area × removal rate × carbon price → IRR, NPV, payback period.',
    projectItems: ['Crediting period: 25 years', 'Carbon price assumptions', 'Setup and monitoring costs'] },
];

// Area from GeoJSON
function polygonAreaHa(coords) {
  if (!coords || coords.length < 3) return 0;
  const R = 6378137;
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const [lng1, lat1] = coords[i];
    const [lng2, lat2] = coords[(i + 1) % coords.length];
    area += (lng2 - lng1) * (Math.PI / 180) * (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180));
  }
  return (Math.abs(area) * R * R / 2) / 10000;
}
function fcAreaHa(fc) {
  if (!fc?.features) return 0;
  return fc.features.reduce((s, f) => {
    const g = f.geometry;
    if (g?.type === 'Polygon') return s + polygonAreaHa(g.coordinates[0]);
    if (g?.type === 'MultiPolygon') return s + g.coordinates.reduce((s2, p) => s2 + polygonAreaHa(p[0]), 0);
    return s;
  }, 0);
}
// Center of GeoJSON
function fcCenter(fc) {
  if (!fc?.features?.length) return null;
  let sumLat = 0, sumLng = 0, n = 0;
  for (const f of fc.features) {
    const g = f.geometry;
    const rings = g?.type === 'Polygon' ? g.coordinates : g?.type === 'MultiPolygon' ? g.coordinates.map(p => p[0]) : [];
    for (const ring of rings) for (const [lng, lat] of ring) { sumLat += lat; sumLng += lng; n++; }
  }
  return n > 0 ? { lat: sumLat / n, lng: sumLng / n } : null;
}

// ---------------------------------------------------------------------------
export default function FeasibilityPage() {
  const { selectedProject, selectedSite, setSelectedSite, availableSites } = useProject();
  const navigate = useNavigate();
  const projectId = selectedProject?.id;

  const [drillPhase, setDrillPhase] = useState(null);
  const [sites, setSites] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [claims, setClaims] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [boundary, setBoundary] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgData, setDlgData] = useState({});
  const [processing, setProcessing] = useState(null);

  const currentSite = selectedSite;
  const isAllSites = !currentSite;
  const boundaryArea = useMemo(() => fcAreaHa(boundary), [boundary]);
  const boundaryCenter = useMemo(() => fcCenter(boundary), [boundary]);

  // --- Loaders ---
  const loadSites = useCallback(() => {
    if (!projectId) return;
    fetch(`/v1/sites-feasibility?projectId=${projectId}`).then(r => r.ok ? r.json() : []).then(setSites).catch(() => setSites([]));
  }, [projectId]);
  const loadStakeholders = useCallback(() => {
    if (!projectId) return;
    fetch(`/v1/stakeholders?projectId=${projectId}`).then(r => r.ok ? r.json() : []).then(setStakeholders).catch(() => setStakeholders([]));
  }, [projectId]);
  const loadClaims = useCallback(() => {
    if (!currentSite?.id) { setClaims([]); return; }
    fetch(`/v1/sites/${currentSite.id}/claims`).then(r => r.ok ? r.json() : []).then(setClaims).catch(() => setClaims([]));
  }, [currentSite?.id]);
  const loadEvidence = useCallback(() => {
    if (!currentSite?.id) { setEvidence([]); return; }
    fetch(`/v1/sites/${currentSite.id}/evidence`).then(r => r.ok ? r.json() : []).then(setEvidence).catch(() => setEvidence([]));
  }, [currentSite?.id]);
  const loadBoundary = useCallback(() => {
    if (!selectedProject?.code) { setBoundary(null); return; }
    const p = new URLSearchParams({ project: selectedProject.code });
    if (currentSite?.code) p.set('site', currentSite.code);
    fetch(`/v1/stratification/geometry?${p}`).then(r => r.ok ? r.json() : null).then(setBoundary).catch(() => setBoundary(null));
  }, [selectedProject?.code, currentSite?.code]);

  useEffect(() => { loadSites(); loadStakeholders(); }, [loadSites, loadStakeholders]);
  useEffect(() => { loadClaims(); loadEvidence(); loadBoundary(); }, [loadClaims, loadEvidence, loadBoundary]);

  // --- Handlers ---
  const selectSite = (site) => setSelectedSite(availableSites.find(s => s.id === site.id) || null);
  const updateSiteField = async (siteId, fields) => {
    await fetch(`/v1/sites/${siteId}/feasibility`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields) });
    loadSites();
  };
  const saveClaim = async (claimKey, status, answer) => {
    if (!currentSite?.id) return;
    await fetch(`/v1/sites/${currentSite.id}/claims`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ claimKey, status, answer }) });
    loadClaims(); loadSites();
  };
  const addEvidence = async () => {
    if (!currentSite?.id) return;
    await fetch(`/v1/sites/${currentSite.id}/evidence`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(dlgData) });
    setDlgOpen(false); loadEvidence();
  };
  const deleteEvidence = async (id) => { await fetch(`/v1/site-evidence/${id}`, { method: 'DELETE' }); loadEvidence(); };

  const openSoilGrids = () => {
    const c = boundaryCenter;
    const url = c ? `https://soilgrids.org/#!/?lon=${c.lng.toFixed(4)}&lat=${c.lat.toFixed(4)}&zoom=14` : 'https://soilgrids.org/';
    window.open(url, '_blank');
  };

  // --- Stats ---
  const totalSites = sites.length;
  const doneSites = sites.filter(s => ((s.claims?.done || 0) + (s.claims?.verified || 0)) >= ALL_CLAIMS.length).length;

  const footer = (
    <Stack direction="row" spacing={2} sx={{ mt: 3, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Link component="button" variant="caption" color="text.secondary" underline="hover" onClick={() => navigate('/project/change-log')}>Change Log</Link>
      <Link component="button" variant="caption" color="text.secondary" underline="hover" onClick={() => navigate('/project/sop')}>SOP</Link>
    </Stack>
  );

  if (!selectedProject) {
    return (<><Helmet><title>Feasibility | TERRRA</title></Helmet><Container maxWidth="lg"><Alert severity="info" sx={{ mt: 3 }}>Select a project.</Alert></Container></>);
  }

  // ================================================================
  // OVERVIEW — 5 phase cards only
  // ================================================================
  if (!drillPhase) {
    return (
      <>
        <Helmet><title>Feasibility | TERRRA</title></Helmet>
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="h5">Feasibility Assessment</Typography>
            <Chip label={`${doneSites}/${totalSites} sites complete`} size="small" color="success" variant="outlined" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{selectedProject.code}</Typography>
          <Stack spacing={2}>
            {PHASES.map(phase => (
              <Card key={phase.key} variant="outlined" sx={{ '&:hover': { boxShadow: 3 } }}>
                <CardActionArea onClick={() => setDrillPhase(phase.key)}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: phase.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Iconify icon={phase.icon} sx={{ width: 24, height: 24, color: phase.color }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6">{phase.label}</Typography>
                        <Typography variant="body2" color="text.secondary">{phase.desc}</Typography>
                      </Box>
                      <Iconify icon="eva:arrow-ios-forward-fill" sx={{ color: 'text.disabled' }} />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
          {footer}
        </Container>
      </>
    );
  }

  // ================================================================
  // BOUNDARY drill-down
  // ================================================================
  if (drillPhase === 'boundary') {
    return (
      <>
        <Helmet><title>Boundary{currentSite ? ` — ${currentSite.code}` : ''} | TERRRA</title></Helmet>
        <Container maxWidth="xl" sx={{ py: 1 }}>
          {/* Breadcrumb */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
            <Link component="button" variant="body2" underline="hover" onClick={() => setDrillPhase(null)}>Feasibility</Link>
            <Iconify icon="eva:chevron-right-outline" sx={{ width: 16, height: 16, color: 'text.disabled' }} />
            <Typography variant="body2" fontWeight={700}>Boundary & Area</Typography>
            {currentSite && (
              <>
                <Iconify icon="eva:chevron-right-outline" sx={{ width: 16, height: 16, color: 'text.disabled' }} />
                <Chip label={currentSite.code} size="small" color="primary" onDelete={() => setSelectedSite(null)} />
              </>
            )}
          </Stack>

          {/* All Sites view — no tools, just map + site list */}
          {isAllSites && (
            <>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <MiniMapPreview key={`bnd-all-${boundary?.features?.length}`} boundary={boundary} height={320} />
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="overline" color="text.secondary">Project-Level</Typography>
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {PHASES[0].projectItems.map((item, i) => (
                      <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary">{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Sites — select to edit boundary</Typography>
                  <Stack spacing={0.5}>
                    {sites.map(site => (
                      <Stack key={site.id} direction="row" spacing={1} alignItems="center"
                        onClick={() => selectSite(site)}
                        sx={{ p: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 70 }}>{site.code}</Typography>
                        <Chip label={site.phase_inclusion || 'current'} size="small" sx={{ fontSize: 10, height: 18 }}
                          color={site.phase_inclusion === 'current' ? 'primary' : 'default'} variant="outlined" />
                        <Typography variant="caption" color="text.secondary">{site.land_use || '—'}</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary">{site.owner_name || ''}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </>
          )}

          {/* Per-site view — site header, map, tools + claims below */}
          {currentSite && (() => {
            const site = sites.find(s => s.id === currentSite.id);
            return (
              <>
                {/* Site header */}
                <Card variant="outlined" sx={{ mb: 1 }}>
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle1" fontWeight={700}>{currentSite.code}</Typography>
                      <Chip label={`${boundaryArea.toFixed(1)} ha`} size="small" color="primary" />
                      <Select size="small" value={site?.phase_inclusion || 'current'}
                        onChange={e => updateSiteField(currentSite.id, { phase_inclusion: e.target.value })}
                        sx={{ fontSize: 11, height: 24 }}>
                        <MenuItem value="current">Current</MenuItem>
                        <MenuItem value="expansion">Expansion</MenuItem>
                        <MenuItem value="candidate">Candidate</MenuItem>
                      </Select>
                      <Select size="small" value={site?.land_use || ''} displayEmpty
                        onChange={e => updateSiteField(currentSite.id, { land_use: e.target.value })}
                        sx={{ fontSize: 11, height: 24 }}>
                        <MenuItem value=""><em>Land use</em></MenuItem>
                        <MenuItem value="cropland">Cropland</MenuItem>
                        <MenuItem value="grassland">Grassland</MenuItem>
                        <MenuItem value="mixed">Mixed</MenuItem>
                      </Select>
                      <Select size="small" value={site?.owner_stakeholder_id || ''} displayEmpty
                        onChange={e => updateSiteField(currentSite.id, { owner_stakeholder_id: e.target.value || null })}
                        sx={{ fontSize: 11, height: 24 }}>
                        <MenuItem value=""><em>Owner</em></MenuItem>
                        {stakeholders.filter(s => s.type === 'landowner' || s.type === 'other').map(s => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                      {(() => { const d = (site?.claims?.done||0)+(site?.claims?.verified||0);
                        return <><LinearProgress variant="determinate" value={Math.round(d/ALL_CLAIMS.length*100)} sx={{ width: 80, height: 5, borderRadius: 3 }} /><Typography variant="caption" sx={{ fontSize: 10 }}>{d}/{ALL_CLAIMS.length}</Typography></>;
                      })()}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Map */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <MiniMapPreview key={`bnd-${currentSite.code}-${boundary?.features?.length}`} boundary={boundary} height={400} />
                  </CardContent>
                </Card>

                {/* Below map: all full width */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Boundary Tools</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:download-outline" />}
                        component="a" href={`/v1/stratification/geometry?project=${selectedProject.code}&site=${currentSite.code}`}
                        download>Download KML</Button>
                      <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:upload-outline" />}
                        component="label">Upload / Replace<input hidden type="file" accept=".kml,.geojson" /></Button>
                      <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:droplet-outline" />}
                        disabled={!!processing}>Remove Water Bodies</Button>
                      <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:grid-outline" />}
                        disabled={!!processing}>Segment Fields</Button>
                      <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:camera-outline" />}>
                        Snapshot as Evidence</Button>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Reference</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button size="small" variant="text" startIcon={<Iconify icon="eva:globe-2-outline" />} onClick={openSoilGrids}>
                        SoilGrids{boundaryCenter ? ' (zoom to site)' : ''}</Button>
                      <Button size="small" variant="text" startIcon={<Iconify icon="eva:layers-outline" />}
                        onClick={() => window.open('https://www.fao.org/soils-portal/data-hub', '_blank')}>FAO Soil Portal</Button>
                      <Button size="small" variant="text" startIcon={<Iconify icon="eva:people-outline" />}
                        onClick={() => navigate('/project/registry/stakeholders')}>Stakeholders</Button>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Boundary Requirements</Typography>
                        <Stack spacing={1} divider={<Divider />}>
                          {BOUNDARY_CLAIMS.map(claim => {
                            const saved = claims.find(c => c.claim_key === claim.key);
                            const claimEv = evidence.filter(e => e.claim_key === claim.key);
                            const st = saved?.status || 'empty';
                            return (
                              <Box key={claim.key}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                    bgcolor: st === 'done' || st === 'verified' ? 'success.main' : st === 'in_progress' ? 'warning.main' : 'grey.400' }} />
                                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{claim.label}</Typography>
                                  <Select size="small" value={st} onChange={e => saveClaim(claim.key, e.target.value, saved?.answer || '')}
                                    sx={{ fontSize: 10, height: 22, minWidth: 90 }}>
                                    {CLAIM_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>)}
                                  </Select>
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>{claim.desc}</Typography>
                                <TextField size="small" fullWidth multiline maxRows={3} placeholder="Notes..."
                                  defaultValue={saved?.answer || ''}
                                  onBlur={e => { if (e.target.value !== (saved?.answer || '')) saveClaim(claim.key, st, e.target.value); }}
                                  sx={{ mt: 0.5, ml: 2, '& .MuiOutlinedInput-root': { fontSize: 11 } }} />
                                {claimEv.map(ev => (
                                  <Stack key={ev.id} direction="row" spacing={0.5} alignItems="center" sx={{ ml: 2, mt: 0.25 }}>
                                    <Iconify icon="eva:attach-outline" sx={{ width: 12, height: 12, color: 'text.secondary' }} />
                                    <Typography variant="caption" sx={{ flex: 1 }}>{ev.url ? <a href={ev.url} target="_blank" rel="noopener">{ev.title}</a> : ev.title}</Typography>
                                    <IconButton size="small" onClick={() => deleteEvidence(ev.id)}><Iconify icon="eva:close-outline" sx={{ width: 12, height: 12 }} /></IconButton>
                                  </Stack>
                                ))}
                                <Button size="small" sx={{ ml: 2, mt: 0.25, fontSize: 10 }}
                                  startIcon={<Iconify icon="eva:plus-outline" sx={{ width: 12, height: 12 }} />}
                                  onClick={() => { setDlgData({ claimKey: claim.key }); setDlgOpen(true); }}>Add evidence</Button>
                              </Box>
                            );
                          })}
                        </Stack>
                      </CardContent>
                    </Card>
              </>
            );
          })()}
          {footer}
        </Container>
        {/* Evidence dialog for boundary claims */}
        <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Evidence</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <TextField size="small" label="Title" fullWidth value={dlgData.title || ''} onChange={e => setDlgData(d => ({ ...d, title: e.target.value }))} />
              <Select size="small" value={dlgData.type || 'document'} fullWidth onChange={e => setDlgData(d => ({ ...d, type: e.target.value }))}>
                <MenuItem value="document">Document</MenuItem><MenuItem value="image">Image</MenuItem><MenuItem value="link">Link</MenuItem><MenuItem value="declaration">Declaration</MenuItem><MenuItem value="parcel_data">Parcel Data</MenuItem>
              </Select>
              <TextField size="small" label="URL (optional)" fullWidth value={dlgData.url || ''} onChange={e => setDlgData(d => ({ ...d, url: e.target.value }))} />
              <TextField size="small" label="Description" multiline rows={2} fullWidth value={dlgData.description || ''} onChange={e => setDlgData(d => ({ ...d, description: e.target.value }))} />
            </Stack>
          </DialogContent>
          <DialogActions><Button onClick={() => setDlgOpen(false)}>Cancel</Button><Button variant="contained" onClick={addEvidence} disabled={!dlgData.title}>Add</Button></DialogActions>
        </Dialog>
      </>
    );
  }

  // ================================================================
  // ELIGIBILITY drill-down
  // ================================================================
  if (drillPhase === 'eligibility') {
    return (
      <>
        <Helmet><title>Eligibility{currentSite ? ` — ${currentSite.code}` : ''} | TERRRA</title></Helmet>
        <Container maxWidth="xl" sx={{ py: 1 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
            <Link component="button" variant="body2" underline="hover" onClick={() => setDrillPhase(null)}>Feasibility</Link>
            <Iconify icon="eva:chevron-right-outline" sx={{ width: 16, height: 16, color: 'text.disabled' }} />
            <Typography variant="body2" fontWeight={700}>Eligibility</Typography>
            {currentSite && (
              <><Iconify icon="eva:chevron-right-outline" sx={{ width: 16, height: 16, color: 'text.disabled' }} /><Chip label={currentSite.code} size="small" color="primary" onDelete={() => setSelectedSite(null)} /></>
            )}
          </Stack>

          {isAllSites ? (
            <>
              {/* Project-level items for eligibility */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="overline" color="text.secondary">Project-Level</Typography>
                  <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                    {PHASES[1].projectItems.map((item, i) => (
                      <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'grey.400', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary">{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Sites — select to review eligibility</Typography>
                  <Stack spacing={0.5}>
                    {sites.map(site => {
                      const done = (site.claims?.done || 0) + (site.claims?.verified || 0);
                      return (
                        <Stack key={site.id} direction="row" spacing={1} alignItems="center" onClick={() => selectSite(site)}
                          sx={{ p: 0.75, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 70 }}>{site.code}</Typography>
                          <Box sx={{ flex: 1 }} />
                          <LinearProgress variant="determinate" value={Math.round(done / ALL_CLAIMS.length * 100)} sx={{ width: 80, height: 5, borderRadius: 3 }} />
                          <Typography variant="caption" sx={{ fontSize: 10 }}>{done}/{CLAIMS.length}</Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Eligibility Requirements — {currentSite.code}</Typography>
                    <Stack spacing={1.5} divider={<Divider />}>
                      {ELIGIBILITY_CLAIMS.map(claim => {
                        const saved = claims.find(c => c.claim_key === claim.key);
                        const claimEv = evidence.filter(e => e.claim_key === claim.key);
                        const st = saved?.status || 'empty';
                        return (
                          <Box key={claim.key}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                bgcolor: st === 'done' || st === 'verified' ? 'success.main' : st === 'in_progress' ? 'warning.main' : 'grey.400' }} />
                              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{claim.label}</Typography>
                              <Select size="small" value={st} onChange={e => saveClaim(claim.key, e.target.value, saved?.answer || '')}
                                sx={{ fontSize: 10, height: 22, minWidth: 90 }}>
                                {CLAIM_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>)}
                              </Select>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>{claim.desc}</Typography>
                            <TextField size="small" fullWidth multiline maxRows={3} placeholder="Notes..."
                              defaultValue={saved?.answer || ''}
                              onBlur={e => { if (e.target.value !== (saved?.answer || '')) saveClaim(claim.key, st, e.target.value); }}
                              sx={{ mt: 0.5, ml: 2, '& .MuiOutlinedInput-root': { fontSize: 11 } }} />
                            {claimEv.map(ev => (
                              <Stack key={ev.id} direction="row" spacing={0.5} alignItems="center" sx={{ ml: 2, mt: 0.25 }}>
                                <Iconify icon="eva:attach-outline" sx={{ width: 12, height: 12, color: 'text.secondary' }} />
                                <Typography variant="caption" sx={{ flex: 1 }}>{ev.url ? <a href={ev.url} target="_blank" rel="noopener">{ev.title}</a> : ev.title}</Typography>
                                <IconButton size="small" onClick={() => deleteEvidence(ev.id)}><Iconify icon="eva:close-outline" sx={{ width: 12, height: 12 }} /></IconButton>
                              </Stack>
                            ))}
                            <Button size="small" sx={{ ml: 2, mt: 0.25, fontSize: 10 }}
                              startIcon={<Iconify icon="eva:plus-outline" sx={{ width: 12, height: 12 }} />}
                              onClick={() => { setDlgData({ claimKey: claim.key }); setDlgOpen(true); }}>Add evidence</Button>
                          </Box>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ width: { xs: '100%', lg: 260 }, flexShrink: 0 }}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Suggested Stakeholders</Typography>
                    {['Landowner', 'Local Authority', 'Environmental Agency', 'VVB', 'Soil Lab'].map(s => (
                      <Typography key={s} variant="caption" color="text.secondary" sx={{ display: 'block' }}>• {s}</Typography>
                    ))}
                    <Button size="small" sx={{ mt: 1, fontSize: 11 }} onClick={() => navigate('/project/registry/stakeholders')}>Open Registry</Button>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Reference</Typography>
                    <Button size="small" fullWidth variant="text" sx={{ justifyContent: 'flex-start', fontSize: 11 }}
                      startIcon={<Iconify icon="eva:globe-2-outline" />} onClick={openSoilGrids}>SoilGrids{boundaryCenter ? ' (site)' : ''}</Button>
                    <Button size="small" fullWidth variant="text" sx={{ justifyContent: 'flex-start', fontSize: 11 }}
                      startIcon={<Iconify icon="eva:thermometer-outline" />} onClick={() => window.open('https://www.ipcc-nggip.iges.or.jp/EFDB/main.php', '_blank')}>IPCC EFDB</Button>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
          {footer}
        </Container>

        <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Evidence</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <TextField size="small" label="Title" fullWidth value={dlgData.title || ''} onChange={e => setDlgData(d => ({ ...d, title: e.target.value }))} />
              <Select size="small" value={dlgData.type || 'document'} fullWidth onChange={e => setDlgData(d => ({ ...d, type: e.target.value }))}>
                <MenuItem value="document">Document</MenuItem><MenuItem value="image">Image</MenuItem><MenuItem value="link">Link</MenuItem><MenuItem value="declaration">Declaration</MenuItem><MenuItem value="parcel_data">Parcel Data</MenuItem>
              </Select>
              <TextField size="small" label="URL (optional)" fullWidth value={dlgData.url || ''} onChange={e => setDlgData(d => ({ ...d, url: e.target.value }))} />
              <TextField size="small" label="Description" multiline rows={2} fullWidth value={dlgData.description || ''} onChange={e => setDlgData(d => ({ ...d, description: e.target.value }))} />
            </Stack>
          </DialogContent>
          <DialogActions><Button onClick={() => setDlgOpen(false)}>Cancel</Button><Button variant="contained" onClick={addEvidence} disabled={!dlgData.title}>Add</Button></DialogActions>
        </Dialog>
      </>
    );
  }

  // ================================================================
  // OTHER DRILL-DOWNS — placeholder with project-level items
  // ================================================================
  const phase = PHASES.find(p => p.key === drillPhase);
  return (
    <>
      <Helmet><title>{phase?.label} | TERRRA</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
          <Link component="button" variant="body2" underline="hover" onClick={() => setDrillPhase(null)}>Feasibility</Link>
          <Iconify icon="eva:chevron-right-outline" sx={{ width: 16, height: 16, color: 'text.disabled' }} />
          <Typography variant="body2" fontWeight={700}>{phase?.label}</Typography>
        </Stack>
        {isAllSites && phase?.projectItems?.length > 0 && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1 }}>
              <Typography variant="overline" color="text.secondary">Project-Level</Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {phase.projectItems.map((item, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'grey.400', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary">{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Iconify icon={phase?.icon} sx={{ width: 32, height: 32, color: phase?.color }} />
              <Box><Typography variant="h6">{phase?.label}</Typography><Typography variant="body2" color="text.secondary">{phase?.desc}</Typography></Box>
            </Stack>
            <Box sx={{ p: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
              <Iconify icon="eva:code-outline" sx={{ width: 40, height: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Interactive tools for {phase?.label} — coming next.</Typography>
            </Box>
          </CardContent>
        </Card>
        {footer}
      </Container>
    </>
  );
}
